import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { 
  FileText, 
  IndianRupee, 
  Download, 
  Printer, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  MessageCircle, 
  X, 
  Phone, 
  Send, 
  Copy, 
  Check, 
  Plus
} from 'lucide-react';

const BillingScreen = () => {
  const { getConfig } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // WhatsApp Modal State
  const [selectedOrderForWhatsApp, setSelectedOrderForWhatsApp] = useState(null);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [copied, setCopied] = useState(false);

  const STORE_WHATSAPP = '9655506070';

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/orders`, getConfig());
      setOrders(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading billing orders:', err);
      setLoading(false);
    }
  }, [getConfig]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Aggregate stats
  const totalRevenue = orders.reduce((acc, order) => acc + (order.isPaid ? order.totalPrice : 0), 0);
  const totalTaxCollected = orders.reduce((acc, order) => acc + (order.isPaid ? order.taxPrice : 0), 0);
  const pendingPayments = orders.reduce((acc, order) => acc + (!order.isPaid ? order.totalPrice : 0), 0);

  const handleExportCSV = () => {
    if (orders.length === 0) return;
    const headers = ['Invoice / Order ID', 'Date', 'Customer', 'Phone', 'Tax', 'Total', 'Type', 'Status'];
    const csvContent = [
      headers.join(','),
      ...orders.map(order => [
        `INV-${order._id.substring(0, 8).toUpperCase()}`,
        new Date(order.createdAt).toLocaleDateString(),
        `"${order.customerName || order.user?.name || 'Guest'}"`,
        order.customerPhone || order.shippingAddress?.phone || order.user?.phone || '',
        order.taxPrice,
        order.totalPrice,
        order.isManualBill ? 'POS Manual' : 'Online Store',
        order.isPaid ? 'Paid' : 'Unpaid'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'billing_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // WhatsApp helper
  const openWhatsAppModal = (order) => {
    setSelectedOrderForWhatsApp(order);
    const phone = 
      order.customerPhone ||
      order.shippingAddress?.phone || 
      order.user?.addresses?.[0]?.phone || 
      order.user?.phone || 
      '';
    setRecipientPhone(phone.replace(/\D/g, ''));
  };

  const generateWhatsAppMessage = (order) => {
    if (!order) return '';
    const orderId = order._id ? order._id.substring(0, 10).toUpperCase() : '';
    const dateStr = new Date(order.createdAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const customerName = order.customerName || order.user?.name || 'Customer';
    const address = `${order.shippingAddress?.address || ''}, ${order.shippingAddress?.city || ''} ${order.shippingAddress?.postalCode || ''}`.trim();

    const itemsText = (order.orderItems || [])
      .map(
        (item, idx) =>
          `${idx + 1}. *${item.name}*\n   Qty: ${item.qty} × ₹${Number(item.price).toFixed(2)} = *₹${(item.qty * item.price).toFixed(2)}*`
      )
      .join('\n\n');

    const discountText =
      order.discountPrice > 0
        ? `\n*Discount:* -₹${Number(order.discountPrice).toFixed(2)}`
        : '';

    const invoiceUrl = `${window.location.origin}/orders/${order._id}/invoice`;

    return `🌿 *ALAIAATHI - Homemade Soaps & Products*
📞 *Contact:* +91 ${STORE_WHATSAPP} | ✉️ support@alaiaathi.com
━━━━━━━━━━━━━━━━━━━━━━━━
🧾 *TAX INVOICE / BILL RECEIPT*
*Receipt No:* #INV-${orderId}
*Date:* ${dateStr}
*Customer:* ${customerName}
*Status:* ${order.isPaid ? 'PAID ✅' : 'PENDING ⏳'}
━━━━━━━━━━━━━━━━━━━━━━━━
${itemsText ? `🛍️ *ORDER ITEMS:*\n\n${itemsText}\n━━━━━━━━━━━━━━━━━━━━━━━━\n` : ''}*Subtotal:* ₹${(order.itemsPrice || 0).toFixed(2)}
*Tax:* ₹${(order.taxPrice || 0).toFixed(2)}${discountText}
*Shipping:* ₹${(order.shippingPrice || 0).toFixed(2)}
💰 *TOTAL AMOUNT:* *₹${(order.totalPrice || 0).toFixed(2)}*
━━━━━━━━━━━━━━━━━━━━━━━━
📍 *Location / Address:*
${address || 'Store Counter / Standard Delivery'}

✨ *Thank you for shopping with Alaiaathi!*
For inquiries, message us at +91 ${STORE_WHATSAPP}.

🔗 *Online Bill & Receipt:*
${invoiceUrl}`;
  };

  const handleSendWhatsApp = (e) => {
    if (e) e.preventDefault();
    let phone = recipientPhone.replace(/\D/g, '');
    
    if (!phone) {
      alert('Please enter the customer WhatsApp phone number.');
      return;
    }

    if (phone.length === 10) {
      phone = `91${phone}`;
    } else if (phone.startsWith('0') && phone.length === 11) {
      phone = `91${phone.substring(1)}`;
    }

    const message = generateWhatsAppMessage(selectedOrderForWhatsApp);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
    setSelectedOrderForWhatsApp(null);
  };

  const handleCopyMessage = () => {
    if (!selectedOrderForWhatsApp) return;
    const message = generateWhatsAppMessage(selectedOrderForWhatsApp);
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="p-8 space-y-10 max-w-7xl mx-auto">
      {/* Header with POS Create Bill Link */}
      <div className="flex flex-wrap gap-4 justify-between items-end">
        <div className="space-y-2">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Billing & Invoices</h1>
          <p className="text-secondary-500 font-medium">Manage financial reports, tax, and customer invoices.</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Link 
            to="/admin/pos"
            className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-2xl font-black uppercase transition-all shadow-lg shadow-emerald-200 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>+ Create New Bill (POS)</span>
          </Link>

          <button 
            onClick={handleExportCSV}
            className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-2xl font-black uppercase transition-all shadow-lg shadow-primary-200 text-sm"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-[32px] p-6 border border-secondary-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500 opacity-5 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500" />
          <div className="bg-emerald-500 p-3 rounded-2xl text-white inline-block mb-4 shadow-lg">
            <IndianRupee className="h-6 w-6" />
          </div>
          <h3 className="text-secondary-400 font-black text-[10px] uppercase tracking-widest mb-1">Total Revenue</h3>
          <p className="text-2xl font-black text-secondary-900 italic">₹{totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-[32px] p-6 border border-secondary-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500 opacity-5 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500" />
          <div className="bg-blue-500 p-3 rounded-2xl text-white inline-block mb-4 shadow-lg">
            <FileText className="h-6 w-6" />
          </div>
          <h3 className="text-secondary-400 font-black text-[10px] uppercase tracking-widest mb-1">Total Tax Collected</h3>
          <p className="text-2xl font-black text-secondary-900 italic">₹{totalTaxCollected.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-[32px] p-6 border border-secondary-100 shadow-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500 opacity-5 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500" />
          <div className="bg-amber-500 p-3 rounded-2xl text-white inline-block mb-4 shadow-lg">
            <Clock className="h-6 w-6" />
          </div>
          <h3 className="text-secondary-400 font-black text-[10px] uppercase tracking-widest mb-1">Pending Payments</h3>
          <p className="text-2xl font-black text-secondary-900 italic">₹{pendingPayments.toFixed(2)}</p>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-[40px] border border-secondary-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-secondary-100 flex items-center justify-between">
           <h2 className="text-lg font-black text-secondary-900 uppercase tracking-tight">Invoice History</h2>
           <span className="text-xs font-bold text-secondary-400">{orders.length} Invoices recorded</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-secondary-50/50">
              <tr className="border-b border-secondary-100">
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Invoice / Order ID</th>
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Date</th>
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Customer</th>
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Type</th>
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Total</th>
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Status</th>
                <th className="px-8 py-5 text-right font-black text-secondary-400 text-[10px] uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-50">
              {loading ? (
                 <tr><td colSpan="7" className="text-center py-20 font-bold text-secondary-400">Loading billing data...</td></tr>
              ) : orders.length === 0 ? (
                 <tr><td colSpan="7" className="text-center py-20 font-bold text-secondary-400 uppercase tracking-widest">No invoices found</td></tr>
              ) : orders.map((order) => (
                <tr key={order._id} className="hover:bg-secondary-50/50 transition-colors group">
                  <td className="px-8 py-5 font-bold text-secondary-900 text-xs">
                    INV-{order._id.substring(0, 8).toUpperCase()}
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center space-x-2 text-secondary-500 text-xs font-medium">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="font-bold text-secondary-900 text-sm">
                        {order.customerName || order.user?.name || 'Walk-in Guest'}
                      </span>
                      {(order.customerPhone || order.shippingAddress?.phone) && (
                        <span className="text-[11px] text-secondary-400 font-medium">
                          {order.customerPhone || order.shippingAddress?.phone}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      order.isManualBill 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {order.isManualBill ? 'POS Manual' : 'Online Store'}
                    </span>
                  </td>
                  <td className="px-8 py-5 font-black text-secondary-900">₹{order.totalPrice}</td>
                  <td className="px-8 py-5">
                    <div className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      order.isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {order.isPaid ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      <span>{order.isPaid ? 'Paid' : 'Unpaid'}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right space-x-2">
                    {/* Send WhatsApp Button */}
                    <button 
                      onClick={() => openWhatsAppModal(order)}
                      className="inline-flex items-center space-x-1.5 p-2 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-all font-bold text-xs"
                      title="Send Bill via WhatsApp"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      <span>WhatsApp</span>
                    </button>

                    {/* Print Button */}
                    <a 
                      href={`/orders/${order._id}/invoice`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1.5 p-2 px-3 bg-secondary-50 hover:bg-primary-50 hover:text-primary-600 text-secondary-600 rounded-xl transition-all font-bold text-xs"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      <span>Print</span>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* WhatsApp Modal for BillingScreen */}
      {selectedOrderForWhatsApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full text-secondary-900 shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setSelectedOrderForWhatsApp(null)}
              className="absolute top-5 right-5 text-secondary-400 hover:text-secondary-700 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-600">
                <MessageCircle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">Send WhatsApp Bill</h3>
                <p className="text-xs text-secondary-500 font-sans">
                  From: <strong className="text-emerald-700">+91 {STORE_WHATSAPP}</strong>
                </p>
              </div>
            </div>

            <form onSubmit={handleSendWhatsApp} className="space-y-4 font-sans">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-600 mb-1.5">
                  Customer WhatsApp Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-secondary-400">
                    <Phone className="h-4 w-4" />
                  </div>
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="e.g. 9876543210 or 919876543210"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-secondary-50 border border-secondary-200 rounded-2xl text-sm font-bold text-secondary-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <p className="text-[11px] text-secondary-400 mt-1">
                  Customer: <strong className="text-secondary-700">{selectedOrderForWhatsApp.customerName || selectedOrderForWhatsApp.user?.name || 'Walk-in'}</strong> (Total: ₹{selectedOrderForWhatsApp.totalPrice})
                </p>
              </div>

              {/* Message preview snippet */}
              <div className="p-3 bg-secondary-50 rounded-2xl border border-secondary-100 text-[11px] text-secondary-600 max-h-36 overflow-y-auto font-mono whitespace-pre-wrap">
                {generateWhatsAppMessage(selectedOrderForWhatsApp)}
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCopyMessage}
                  className="flex-1 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  <span>{copied ? 'Copied!' : 'Copy Bill'}</span>
                </button>

                <button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors shadow-lg shadow-emerald-200"
                >
                  <Send className="h-4 w-4" />
                  <span>Send Now</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingScreen;
